import ImageQualityStatus from '../../../enums/ImageQualityStatus.js';
import { isAliasingReduction } from '../../../utilities/voxelGrid/index.js';
import { fidelityStateRank, isInteractiveLod, isReduced, lineHasData, lineKind, standingSampleRatioOf, svgStateOf, } from './fidelityIndicatorState.js';
export function buildViewportFidelityState(lines, displaySpacing) {
    const line = worstLineOf(lines);
    if (!line) {
        return emptyState(displaySpacing);
    }
    const shownState = svgStateOf(lines);
    const { record, reductionFactors, imageQualityStatus } = line;
    const shownSpacing = line.displaySpacing ?? displaySpacing;
    const spatiallyReduced = isReduced(reductionFactors);
    const kind = lineKind(line);
    const loading = kind === 'loading';
    const lod = kind === 'lod';
    const lossy = isLossyShownState(shownState);
    const state = {
        dataResolution: record
            ? {
                spacing: [...record.grid.spacing],
                dimensions: [...record.grid.dimensions],
            }
            : undefined,
        displayResolution: shownSpacing
            ? { spacing: [...shownSpacing] }
            : undefined,
        reductionKind: record?.reduction,
        reductionSource: record?.source,
        magnitude: lossy ? [...reductionFactors] : undefined,
        cause: lossy
            ? causeOf({
                record,
                imageQualityStatus,
                reductionCause: line.reductionCause,
                spatiallyReduced,
                standingSample: isInteractiveLod(standingSampleRatioOf(line)),
                loading,
                lod,
            })
            : undefined,
        transient: lossy ? loading || lod : undefined,
        progressive: progressiveOf(line),
        shownState,
        lossy,
    };
    return state;
}
function emptyState(displaySpacing) {
    return {
        dataResolution: undefined,
        displayResolution: displaySpacing
            ? { spacing: [...displaySpacing] }
            : undefined,
        reductionKind: undefined,
        reductionSource: undefined,
        magnitude: undefined,
        cause: undefined,
        transient: undefined,
        progressive: undefined,
        shownState: 'hidden',
        lossy: false,
    };
}
function isLossyShownState(shownState) {
    return (shownState === 'loading' || shownState === 'lod' || shownState === 'lossy');
}
function worstLineOf(lines) {
    let worst;
    let worstRank = 0;
    for (const line of lines) {
        if (!lineHasData(line)) {
            continue;
        }
        const rank = fidelityStateRank(lineKind(line));
        if (rank > worstRank ||
            (rank === worstRank && !worst.record && line.record)) {
            worst = line;
            worstRank = rank;
        }
    }
    return worst;
}
function progressiveOf(line) {
    const { record, imageQualityStatus } = line;
    if (record) {
        const loadedFraction = record.voxels > 0
            ? (record.voxels - record.missing) / record.voxels
            : undefined;
        return {
            status: record.status,
            lowest: record.lowest,
            missing: record.missing,
            voxels: record.voxels,
            loadedFraction,
        };
    }
    if (imageQualityStatus !== undefined) {
        return {
            status: imageQualityStatus,
        };
    }
    return undefined;
}
function causeOf(args) {
    const { record, imageQualityStatus, reductionCause, spatiallyReduced, standingSample, loading, lod, } = args;
    if (loading) {
        return 'incompleteLoad';
    }
    if (lod) {
        return 'performanceTarget';
    }
    const spatial = spatiallyReduced || (record && isAliasingReduction(record.reduction));
    if (spatial && reductionCause) {
        return reductionCause;
    }
    const delivered = record ? record.lowest : imageQualityStatus;
    if (delivered !== undefined &&
        delivered < ImageQualityStatus.FULL_RESOLUTION &&
        delivered >= ImageQualityStatus.SUBRESOLUTION) {
        return 'lossyDecode';
    }
    return standingSample && !spatial ? 'performanceTarget' : undefined;
}
export function viewportFidelityStateKey(state) {
    return JSON.stringify({
        shownState: state.shownState,
        lossy: state.lossy,
        dataResolution: state.dataResolution,
        reductionKind: state.reductionKind,
        reductionSource: state.reductionSource,
        magnitude: state.magnitude,
        cause: state.cause,
        transient: state.transient,
        progressive: state.lossy ? state.progressive : undefined,
    });
}
